import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Security,
  ContentCopy,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import twoFactorService from '../../services/twoFactorService';
import { TwoFactorSetup as TwoFactorSetupType } from '../../types/auth';

interface LocationState {
  from?: string;
  userId?: number;
}

const TwoFactorSetup: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Setup process states
  const [activeStep, setActiveStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  
  const state = location.state as LocationState;
  
  useEffect(() => {
    if (!user && !state?.userId) {
      navigate('/login', { replace: true });
      return;
    }
    
    // Start the setup process
    initiateSetup();
  }, [user, state, navigate]);

  const initiateSetup = async () => {
    setLoading(true);
    setError('');
    
    try {
      const setupData: TwoFactorSetupType = await twoFactorService.setupTwoFactor();
      setQrCode(setupData.qrCode);
      setManualEntryKey(setupData.manualEntryKey);
      setSecret(setupData.secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initiate 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode) {
      setError('Verification code is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await twoFactorService.enableTwoFactor(
        secret,
        verificationCode
      );
      
      if (result.backupCodes) {
        setBackupCodes(result.backupCodes);
        setSuccess('Two-factor authentication has been successfully enabled!');
        handleNext(); // Move to backup codes step
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleFinishSetup = () => {
    // Navigate to where the user came from, or dashboard
    const returnPath = state?.from || '/';
    navigate(returnPath, { replace: true });
  };

  const handleSkipSetup = () => {
    // Navigate back to login to complete the authentication process
    const returnPath = state?.from || '/';
    navigate(returnPath, { replace: true });
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const steps = [
    {
      label: 'Scan QR Code',
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </Typography>
          {qrCode && (
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <img 
                src={qrCode} 
                alt="QR Code for 2FA Setup"
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '200px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: 'white'
                }}
              />
            </Box>
          )}
          <Typography variant="body2" sx={{ mb: 1 }}>
            Or enter this key manually:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              value={manualEntryKey}
              InputProps={{ readOnly: true }}
              size="small"
            />
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={() => copyToClipboard(manualEntryKey)}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )
    },
    {
      label: 'Verify Setup',
      content: (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app to verify the setup.
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="123456"
            inputProps={{ maxLength: 6 }}
            sx={{ mb: 2 }}
          />
        </Box>
      )
    },
    {
      label: 'Backup Codes',
      content: (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
          </Alert>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {backupCodes.map((code, index) => (
              <Grid item xs={6} key={index}>
                <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'grey.100' }}>
                  <Typography variant="body2" fontFamily="monospace">
                    {code}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => copyToClipboard(backupCodes.join('\\n'))}
            startIcon={<ContentCopy />}
            sx={{ mb: 2 }}
          >
            Copy All Backup Codes
          </Button>
        </Box>
      )
    }
  ];

  if (loading && activeStep === 0) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 600,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleSkipSetup} sx={{ mr: 1 }}>
            <ArrowBack />
          </IconButton>
          <Security color="primary" sx={{ mr: 1 }} />
          <Typography variant="h5" component="h1">
            Set Up Two-Factor Authentication
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                {step.content}
                <Box sx={{ mb: 2, mt: 2 }}>
                  <div>
                    {index === 0 && (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Continue
                      </Button>
                    )}
                    {index === 1 && (
                      <>
                        <Button
                          variant="contained"
                          onClick={handleVerifyAndEnable}
                          disabled={loading || !verificationCode}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          {loading ? <CircularProgress size={20} /> : 'Verify & Enable'}
                        </Button>
                        <Button
                          onClick={handleBack}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          Back
                        </Button>
                      </>
                    )}
                    {index === 2 && (
                      <Button
                        variant="contained"
                        onClick={handleFinishSetup}
                        startIcon={<CheckCircle />}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Complete Setup
                      </Button>
                    )}
                    <Button
                      onClick={handleSkipSetup}
                      color="inherit"
                      sx={{ mt: 1, mr: 1 }}
                    >
                      Skip for Now
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Paper>
    </Box>
  );
};

export default TwoFactorSetup;